import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CampaignDataGrid = ({ selectedCampaign, onCampaignSelect }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  const campaignMetrics = [
    {
      id: 'ai-facebook-001',
      name: 'Facebook Auto Leads',
      status: 'active',
      trafficSource: 'Social',
      callVolume: 847,
      conversionRate: 23.5,
      eRPC: 14.70,
      bidWinPercentage: 78.5,
      revenue: 12450.75,
      cost: 8934.50,
      margin: 3516.25,
      avgBidPrice: 12.85,
      topBuyer: 'Premium Insurance Co.',
      lastCall: new Date(Date.now() - 2 * 60 * 1000),
      trend: 'up',
      geography: 'CA, TX, FL',
      demographics: '25-45',
      qualityScore: 8.7
    },
    {
      id: 'ai-google-002',
      name: 'Google Search Auto',
      status: 'active',
      trafficSource: 'Search',
      callVolume: 623,
      conversionRate: 28.1,
      eRPC: 15.85,
      bidWinPercentage: 82.1,
      revenue: 9876.25,
      cost: 6234.80,
      margin: 3641.45,
      avgBidPrice: 14.20,
      topBuyer: 'SafeGuard Insurance',
      lastCall: new Date(Date.now() - 5 * 60 * 1000),
      trend: 'up',
      geography: 'NY, NJ, CT',
      demographics: '30-55',
      qualityScore: 9.2
    },
    {
      id: 'ai-tv-003',
      name: 'TV Commercial Auto',
      status: 'paused',
      trafficSource: 'TV',
      callVolume: 234,
      conversionRate: 18.7,
      eRPC: 13.87,
      bidWinPercentage: 65.3,
      revenue: 3245.50,
      cost: 2456.75,
      margin: 788.75,
      avgBidPrice: 11.95,
      topBuyer: 'AutoProtect Plus',
      lastCall: new Date(Date.now() - 60 * 60 * 1000),
      trend: 'down',
      geography: 'IL, OH, MI',
      demographics: '35-65',
      qualityScore: 7.4
    },
    {
      id: 'hi-direct-001',
      name: 'Direct Mail Home',
      status: 'active',
      trafficSource: 'Direct Mail',
      callVolume: 456,
      conversionRate: 31.2,
      eRPC: 17.15,
      bidWinPercentage: 89.2,
      revenue: 7823.40,
      cost: 4567.20,
      margin: 3256.20,
      avgBidPrice: 15.75,
      topBuyer: 'HomeSecure Insurance',
      lastCall: new Date(Date.now() - 1 * 60 * 1000),
      trend: 'up',
      geography: 'WA, OR, CA',
      demographics: '40-70',
      qualityScore: 9.5
    },
    {
      id: 'hi-radio-002',
      name: 'Radio Home Leads',
      status: 'active',
      trafficSource: 'Radio',
      callVolume: 312,
      conversionRate: 25.8,
      eRPC: 14.64,
      bidWinPercentage: 73.8,
      revenue: 4567.80,
      cost: 3234.50,
      margin: 1333.30,
      avgBidPrice: 13.45,
      topBuyer: 'Family First Insurance',
      lastCall: new Date(Date.now() - 8 * 60 * 1000),
      trend: 'stable',
      geography: 'TX, AZ, NV',
      demographics: '25-50',
      qualityScore: 8.1
    },
    {
      id: 'li-social-001',
      name: 'Social Media Life',
      status: 'active',
      trafficSource: 'Social',
      callVolume: 189,
      conversionRate: 22.4,
      eRPC: 15.53,
      bidWinPercentage: 76.4,
      revenue: 2934.60,
      cost: 2145.30,
      margin: 789.30,
      avgBidPrice: 14.85,
      topBuyer: 'LifeGuard Insurance',
      lastCall: new Date(Date.now() - 12 * 60 * 1000),
      trend: 'up',
      geography: 'FL, GA, SC',
      demographics: '30-60',
      qualityScore: 8.8
    }
  ];

  const columns = [
    { key: 'name', label: 'Campaign Name', sortable: true, width: '200px' },
    { key: 'status', label: 'Status', sortable: true, width: '100px' },
    { key: 'trafficSource', label: 'Source', sortable: true, width: '120px' },
    { key: 'callVolume', label: 'Calls', sortable: true, width: '80px' },
    { key: 'conversionRate', label: 'Conv %', sortable: true, width: '80px' },
    { key: 'eRPC', label: 'eRPC', sortable: true, width: '80px' },
    { key: 'bidWinPercentage', label: 'Win %', sortable: true, width: '80px' },
    { key: 'revenue', label: 'Revenue', sortable: true, width: '120px' },
    { key: 'margin', label: 'Margin', sortable: true, width: '120px' },
    { key: 'avgBidPrice', label: 'Avg Bid', sortable: true, width: '90px' },
    { key: 'topBuyer', label: 'Top Buyer', sortable: true, width: '150px' },
    { key: 'lastCall', label: 'Last Call', sortable: true, width: '100px' },
    { key: 'qualityScore', label: 'Quality', sortable: true, width: '80px' }
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleRowSelect = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected?.has(id)) {
      newSelected?.delete(id);
    } else {
      newSelected?.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows?.size === campaignMetrics?.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(campaignMetrics.map(c => c.id)));
    }
  };

  const sortedData = [...campaignMetrics]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return timestamp?.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-success/10 text-success',
      paused: 'bg-warning/10 text-warning',
      stopped: 'bg-error/10 text-error'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <Icon name="TrendingUp" size={14} className="text-success" />;
      case 'down':
        return <Icon name="TrendingDown" size={14} className="text-error" />;
      default:
        return <Icon name="Minus" size={14} className="text-muted-foreground" />;
    }
  };

  const getQualityColor = (score) => {
    if (score >= 9) return 'text-success';
    if (score >= 7) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="h-full bg-card flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <h2 className="text-lg font-semibold text-foreground">Campaign Performance</h2>
            <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">
              {campaignMetrics?.length} campaigns
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 text-muted-foreground hover:text-foreground transition-smooth">
              <Icon name="Filter" size={16} />
            </button>
            <button className="p-2 text-muted-foreground hover:text-foreground transition-smooth">
              <Icon name="Download" size={16} />
            </button>
            <button className="p-2 text-muted-foreground hover:text-foreground transition-smooth">
              <Icon name="RefreshCw" size={16} />
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedRows?.size > 0 && (
          <div className="flex items-center justify-between p-3 bg-accent/5 border border-accent/20 rounded-lg">
            <span className="text-sm text-foreground">
              {selectedRows?.size} campaign{selectedRows?.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <button className="px-3 py-1 bg-primary text-primary-foreground text-xs rounded hover:bg-primary/90 transition-smooth">
                Activate
              </button>
              <button className="px-3 py-1 bg-warning text-warning-foreground text-xs rounded hover:bg-warning/90 transition-smooth">
                Pause
              </button>
              <button className="px-3 py-1 bg-secondary text-secondary-foreground text-xs rounded hover:bg-secondary/90 transition-smooth">
                Export
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Data Grid */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 p-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedRows?.size === campaignMetrics?.length}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              {columns?.map((column) => (
                <th
                  key={column?.key}
                  className="p-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
                  style={{ width: column?.width }}
                  onClick={() => column?.sortable && handleSort(column?.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column?.label}</span>
                    {column?.sortable && (
                      <Icon
                        name={
                          sortConfig?.key === column?.key
                            ? sortConfig?.direction === 'asc' ?'ChevronUp' :'ChevronDown' :'ChevronsUpDown'
                        }
                        size={12}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedData?.map((campaign) => (
              <tr
                key={campaign?.id}
                className={`hover:bg-muted/30 transition-smooth cursor-pointer ${
                  selectedCampaign?.id === campaign?.id ? 'bg-primary/5' : ''
                }`}
                onClick={() => onCampaignSelect(campaign)}
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedRows?.has(campaign?.id)}
                    onChange={() => handleRowSelect(campaign?.id)}
                    onClick={(e) => e?.stopPropagation()}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="Phone" size={14} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground truncate">
                      {campaign?.name}
                    </span>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(campaign?.status)}`}>
                    {campaign?.status}
                  </span>
                </td>
                <td className="p-3 text-sm text-foreground">{campaign?.trafficSource}</td>
                <td className="p-3 text-sm font-medium text-foreground">{campaign?.callVolume}</td>
                <td className="p-3 text-sm text-foreground">{campaign?.conversionRate}%</td>
                <td className="p-3 text-sm font-medium text-foreground">${campaign?.eRPC}</td>
                <td className="p-3">
                  <div className="flex items-center space-x-1">
                    <span className="text-sm text-foreground">{campaign?.bidWinPercentage}%</span>
                    {getTrendIcon(campaign?.trend)}
                  </div>
                </td>
                <td className="p-3 text-sm font-medium text-success">
                  {formatCurrency(campaign?.revenue)}
                </td>
                <td className="p-3 text-sm font-medium text-accent">
                  {formatCurrency(campaign?.margin)}
                </td>
                <td className="p-3 text-sm text-foreground">${campaign?.avgBidPrice}</td>
                <td className="p-3 text-sm text-foreground truncate">{campaign?.topBuyer}</td>
                <td className="p-3 text-xs text-muted-foreground">
                  {formatTimestamp(campaign?.lastCall)}
                </td>
                <td className="p-3">
                  <span className={`text-sm font-medium ${getQualityColor(campaign?.qualityScore)}`}>
                    {campaign?.qualityScore}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, campaignMetrics?.length)} of {campaignMetrics?.length} campaigns
          </span>
          <div className="flex items-center space-x-2">
            <button
              disabled={currentPage === 1}
              className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50 transition-smooth"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <span className="px-3 py-1 bg-primary text-primary-foreground text-sm rounded">
              {currentPage}
            </span>
            <button
              disabled={currentPage * pageSize >= campaignMetrics?.length}
              className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50 transition-smooth"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDataGrid;